import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { z } from 'zod';

// URL is the single source of truth. Each call to `set()` navigates with
// `replace: true` so TanStack Router's `useSearch` re-runs in every
// subscribing component — chrome + body components that both use this hook
// stay in sync via the router's search reference, not via co-located state.
// History gets one entry per filter change (back/forward semantics intact).
//
// Earlier this file wrote via `window.history.replaceState` to avoid loader
// reruns; that worked for a single consumer but silently broke siblings
// (e.g. `<NetworksChrome>` setting `scope=mine` while `<NetworksListBody>`
// kept its initial `scope=all` cache), because `useSearch` does not
// observe raw `replaceState`.

function searchToParams(search: Record<string, unknown>): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const v of value) params.append(key, String(v));
    } else {
      params.set(key, String(value));
    }
  }
  return params;
}

export type FilterParamsConfig<T extends Record<string, unknown>> = {
  // Accepts plain schemas and ZodEffects (transformed schemas).
  schema: z.ZodType<T, z.ZodTypeDef, unknown>;
  defaults: T;
  debounce?: {
    keys: ReadonlyArray<keyof T & string>;
    ms?: number;
  };
  /**
   * URL keys outside the schema to drop on every set/clear. The list-page
   * pattern: pagination depth (`?depth=N`) lives in the URL but is not part
   * of the filter schema; any filter change must reset it to 1 (i.e. remove
   * it) so "Load more" doesn't bleed across filter views.
   */
  resetKeys?: ReadonlyArray<string>;
};

export type FilterParamsApi<T> = {
  values: T;
  set: (partial: Partial<T>) => void;
  flush: () => void;
  clear: () => void;
};

const DEFAULT_DEBOUNCE_MS = 250;

function shallowEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }
  return false;
}

function paramsToInput(
  sp: URLSearchParams,
): Record<string, string | string[]> {
  const out: Record<string, string | string[]> = {};
  const seen = new Set<string>();
  for (const key of sp.keys()) {
    if (seen.has(key)) continue;
    seen.add(key);
    const all = sp.getAll(key);
    const first = all[0];
    if (all.length === 1 && first !== undefined) out[key] = first;
    else if (all.length > 1) out[key] = all;
  }
  return out;
}

function mergeSearchObject<T extends Record<string, unknown>>(
  current: Record<string, unknown>,
  next: T,
  defaults: T,
  resetKeys: ReadonlyArray<string> = [],
): Record<string, unknown> {
  // Start from the current search so anything outside the schema (e.g. a
  // page-level key like `?c=xxx`) survives our writes.
  const out: Record<string, unknown> = { ...current };
  for (const key of Object.keys(next)) delete out[key];
  for (const key of resetKeys) delete out[key];
  for (const [key, value] of Object.entries(next)) {
    const def = (defaults as Record<string, unknown>)[key];
    if (shallowEqual(value, def)) continue;
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      out[key] = value.map((v) => String(v));
      continue;
    }
    if (typeof value === 'boolean') {
      if (value) out[key] = true;
      continue;
    }
    out[key] = value;
  }
  return out;
}

export function useFilterParams<T extends Record<string, unknown>>(
  config: FilterParamsConfig<T>,
): FilterParamsApi<T> {
  const navigate = useNavigate();
  const searchRaw = useSearch({ strict: false }) as Record<string, unknown>;
  const sp = useMemo(() => searchToParams(searchRaw), [searchRaw]);

  // Stash config in a ref so callbacks/effects don't churn when callers pass a
  // fresh object literal.
  const configRef = useRef(config);
  configRef.current = config;
  const spRef = useRef(sp);
  spRef.current = sp;
  const searchRawRef = useRef(searchRaw);
  searchRawRef.current = searchRaw;

  const parseFromUrl = useCallback((source: URLSearchParams): T => {
    const cfg = configRef.current;
    const result = cfg.schema.safeParse(paramsToInput(source));
    return result.success ? result.data : cfg.defaults;
  }, []);

  // Local state is the source of truth after mount. Initialized from URL once.
  const [committed, setCommitted] = useState<T>(() => parseFromUrl(sp));

  // Real navigations (link clicks, popstate) bump the useSearchParams reference;
  // when that happens, resync local state from the new URL.
  const lastSpRef = useRef(sp);
  useEffect(() => {
    if (lastSpRef.current === sp) return;
    lastSpRef.current = sp;
    setCommitted(parseFromUrl(sp));
  }, [sp, parseFromUrl]);

  // Carry a local override only for debounced keys mid-typing.
  const pendingPartial = useRef<Partial<T> | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [tick, setTick] = useState(0);

  const values = useMemo<T>(
    () =>
      pendingPartial.current === null
        ? committed
        : { ...committed, ...pendingPartial.current },
    // tick is the re-render trigger when pendingPartial (a ref) mutates;
    // including it in the dep list is intentional — the linter can't see
    // through the ref read so it flags it as unused.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [committed, tick],
  );

  const debounceMs = config.debounce?.ms ?? DEFAULT_DEBOUNCE_MS;
  const debouncedKeys = useMemo(
    () => config.debounce?.keys ?? [],
    [config.debounce?.keys],
  );

  const writeUrl = useCallback(
    (next: T) => {
      const cfg = configRef.current;
      // navigate({search}) updates TanStack Router's search state — every
      // subscribing `useSearch()` re-runs, including sibling components
      // that use this hook. `replace: true` keeps history bounded.
      navigate({
        to: '.',
        search: mergeSearchObject(
          searchRawRef.current,
          next,
          cfg.defaults,
          cfg.resetKeys,
        ),
        replace: true,
      });
    },
    [navigate],
  );

  const commit = useCallback(
    (next: T) => {
      setCommitted(next);
      writeUrl(next);
    },
    [writeUrl],
  );

  const clearTimer = () => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    pendingPartial.current = null;
  };

  const flush = useCallback(() => {
    if (timer.current === null) return;
    const partial = pendingPartial.current;
    clearTimer();
    if (partial !== null) {
      commit({ ...committed, ...partial });
    }
  }, [commit, committed]);

  const set = useCallback(
    (partial: Partial<T>) => {
      const partialKeys = Object.keys(partial);
      const onlyDebounced =
        partialKeys.length > 0 &&
        partialKeys.every((k) => debouncedKeys.includes(k as keyof T & string));

      if (onlyDebounced) {
        pendingPartial.current = { ...pendingPartial.current, ...partial };
        setTick((t) => t + 1);
        if (timer.current !== null) clearTimeout(timer.current);
        timer.current = setTimeout(() => {
          const queued = pendingPartial.current;
          timer.current = null;
          pendingPartial.current = null;
          if (queued !== null) commit({ ...committed, ...queued });
        }, debounceMs);
        return;
      }

      // Non-debounced change — drop any pending debounced update and commit
      // immediately with merged state.
      const merged = { ...committed, ...pendingPartial.current, ...partial };
      clearTimer();
      commit(merged);
    },
    [committed, commit, debouncedKeys, debounceMs],
  );

  const clear = useCallback(() => {
    clearTimer();
    setTick((t) => t + 1);
    commit(configRef.current.defaults);
  }, [commit]);

  // Flush on unmount so a pending debounced commit (e.g. a search query typed
  // just before navigating away) is written to the URL before it disappears.
  useEffect(() => {
    return () => {
      if (timer.current === null) return;
      clearTimeout(timer.current);
      const partial = pendingPartial.current;
      timer.current = null;
      pendingPartial.current = null;
      if (partial === null) return;
      const cfg = configRef.current;
      const next = { ...parseFromUrl(spRef.current), ...partial };
      navigate({
        to: '.',
        search: mergeSearchObject(
          searchRawRef.current,
          next,
          cfg.defaults,
          cfg.resetKeys,
        ),
        replace: true,
      });
    };
  }, [navigate, parseFromUrl]);

  return { values, set, flush, clear };
}
